// sessionManager.js
import dayjs from "dayjs";
import { extendToken_API } from "../controller/authController";

class SessionManager {
  constructor() {
    // ---- Configuration (all durations in MILLISECONDS unless noted) ----
    this.INACTIVITY_TIMEOUT   = 10 * 60 * 1000; // 10 min idle → show warning
    this.WARNING_BEFORE_EXPIRY = 10 * 60 * 1000; // warn when <= 10 min left on token
    this.COUNTDOWN_DURATION   = 60 * 1000;       // 60 s countdown dialog (ms)
    this.ACTIVITY_DEBOUNCE    = 1000;
    this.COUNTDOWN_UPDATE_INTERVAL = 500;        // ms
    this.TOKEN_CHECK_INTERVAL = 5000;            // ms

    // ---- Cross-tab keys ----
    this.CROSS_TAB_SYNC_KEY    = "app_session_sync";
    this.CROSS_TAB_LOGOUT_KEY  = "app_session_logout";
    this.CROSS_TAB_EXTEND_KEY  = "app_session_extend";

    // ---- State ----
    this.inactivityTimer   = null;
    this.countdownTimer    = null;
    this.tokenCheckInterval = null;
    this.isDialogVisible   = false;
    this.isInitialized     = false;
    this.countdownSeconds  = this.COUNTDOWN_DURATION / 1000;
    this.isExtending       = false;
    this.lastActivityTime  = Date.now();
    this.tabId             = null;
    this.countdownStartTime = null;
    this.lastCountdownUpdate = null;
    this.isUserActive      = true;
    this.lastDialogShowTime = null;
    this.isLoggingOut      = false;
    this._countdownCallback = null;

    // ---- Test mode ----
    this._testMode      = false;
    this._testStartTime = null;
    this._testDuration  = 60; // seconds

    // ---- Event listeners ----
    this.events = ["mousedown", "keydown", "scroll", "click", "mousemove", "touchstart"];

    // ---- Callbacks ----
    this.onSessionExpired  = null;
    this.onShowDialog      = null;
    this.onHideDialog      = null;
    this.onTokenExpiring   = null;
    this.onCrossTabLogout  = null;
    this.onCrossTabExtend  = null;
    this.onUserActivity    = null;

    // ---- Bound handlers (store references so removeEventListener works) ----
    this._boundHandlers = {
      activity:       this.handleUserActivity.bind(this),
      visibility:     this.handleVisibilityChange.bind(this),
      beforeUnload:   this.handleBeforeUnload.bind(this),
      storage:        this.handleStorageEvent.bind(this),
    };

    // Detect test mode (but do NOT start it — just read flags)
    this._readTestModeFlags();
  }

  /* =========================================================
   *  TEST MODE
   * ========================================================= */

  _readTestModeFlags() {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get("test") === "true";
    const testModeStorage = sessionStorage.getItem("test_mode") === "true";
    const testModeLocal   = localStorage.getItem("test_mode") === "true";

    this._testMode = testParam || testModeStorage || testModeLocal;

    if (this._testMode) {
      const saved = sessionStorage.getItem("test_start_time");
      this._testStartTime = saved ? parseInt(saved, 10) : Date.now();
      sessionStorage.setItem("test_start_time", String(this._testStartTime));

      // Override config for test
      this.INACTIVITY_TIMEOUT    = 30 * 1000; // 30 s idle
      this.WARNING_BEFORE_EXPIRY = 10 * 1000; // warn 10 s before token expiry
      this.COUNTDOWN_DURATION    = 60 * 1000; // 60 s dialog (ms)

      console.log(
        ` TEST MODE ENABLED: session will expire in ${this._testDuration}s`
      );
    }
  }

  getTestRemainingSeconds() {
    if (!this._testMode || !this._testStartTime) return -1;
    const elapsed = (Date.now() - this._testStartTime) / 1000;
    return Math.max(0, this._testDuration - elapsed);
  }

  enableTestMode() {
    this._testMode      = true;
    this._testStartTime = Date.now();
    this._testDuration  = 60;

    sessionStorage.setItem("test_mode", "true");
    sessionStorage.setItem("test_start_time", String(this._testStartTime));
    localStorage.setItem("test_mode", "true");

    this.INACTIVITY_TIMEOUT    = 30 * 1000;
    this.WARNING_BEFORE_EXPIRY = 10 * 1000;
    this.COUNTDOWN_DURATION    = 60 * 1000;

    console.log(" Test mode enabled: 60 s expiry");
    if (this.isInitialized) this.resetInactivityTimer();
    return this;
  }

  disableTestMode() {
    this._testMode      = false;
    this._testStartTime = null;

    sessionStorage.removeItem("test_mode");
    sessionStorage.removeItem("test_start_time");
    localStorage.removeItem("test_mode");

    this.INACTIVITY_TIMEOUT    = 10 * 60 * 1000;
    this.WARNING_BEFORE_EXPIRY = 10 * 60 * 1000;
    this.COUNTDOWN_DURATION    = 60 * 1000;

    console.log(" Production mode enabled: 10 min idle timeout");
    if (this.isInitialized) this.resetInactivityTimer();
    return this;
  }

  /* =========================================================
   *  LIFECYCLE
   * ========================================================= */

  init(callbacks) {
    if (this.isInitialized) {
      console.log("SessionManager already initialized");
      return;
    }

    this.initializeTabId();

    // ---- FIX: only report "expired" when test mode is actually on & elapsed ----
    const testRemaining =
      this._testMode && this._testStartTime
        ? this.getTestRemainingSeconds()
        : null;

    console.log("SessionManager initialized with:", {
      inactivityTimeout: this.INACTIVITY_TIMEOUT / 1000 + " seconds",
      countdownDuration: this.COUNTDOWN_DURATION / 1000 + " seconds",
      warningBeforeExpiry: this.WARNING_BEFORE_EXPIRY / 1000 + " seconds",
      tabId: this.tabId,
      testMode: this._testMode,
      testRemaining:
        testRemaining === null
          ? "n/a"
          : testRemaining > 0
          ? testRemaining + " seconds"
          : "expired",
    });

    this.onSessionExpired = callbacks.onSessionExpired;
    this.onShowDialog     = callbacks.onShowDialog;
    this.onHideDialog     = callbacks.onHideDialog;
    this.onTokenExpiring  = callbacks.onTokenExpiring;
    this.onCrossTabLogout = callbacks.onCrossTabLogout;
    this.onCrossTabExtend = callbacks.onCrossTabExtend;
    this.onUserActivity   = callbacks.onUserActivity;

    this.isInitialized   = true;
    this.isUserActive    = true;
    this.isLoggingOut    = false;

    this.attachEventListeners();
    this.setupCrossTabSync();

    // ---- FIX: bail out early if there's no session to guard ----
    const token = localStorage.getItem("access-token");
    const profile = this.getProfileDetails();
    if (!token || !profile?.validTill) {
      console.warn(`Tab ${this.tabId}: No valid session found at init`);
      this.isInitialized = false;
      if (this.onSessionExpired) this.onSessionExpired();
      return;
    }

    this.syncSessionFromStorage();
    this.startTokenExpiryCheck();
  }

  initializeTabId() {
    const stored = sessionStorage.getItem("app_tab_id");
    if (stored) {
      this.tabId = stored;
    } else {
      this.tabId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem("app_tab_id", this.tabId);
    }
  }

  destroy() {
    console.log(`Tab ${this.tabId}: Destroying SessionManager`);
    this.isInitialized = false;
    this.isLoggingOut  = true;

    this.stopCountdown();

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    this.removeEventListeners();
    window.removeEventListener("storage", this._boundHandlers.storage);

    this.onSessionExpired  = null;
    this.onShowDialog      = null;
    this.onHideDialog      = null;
    this.onTokenExpiring   = null;
    this.onCrossTabLogout  = null;
    this.onCrossTabExtend  = null;
    this.onUserActivity    = null;

    this.isDialogVisible = false;
    this.isExtending     = false;

    // ---- FIX: reset flag synchronously; nothing async left to await ----
    this.isLoggingOut = false;
  }

  /* =========================================================
   *  PROFILE / TOKEN
   * ========================================================= */

  getProfileDetails() {
    try {
      const realProfile = JSON.parse(
        localStorage.getItem("profileDetails") || "{}"
      );

      if (this._testMode && this._testStartTime) {
        const remaining = this.getTestRemainingSeconds();
        if (remaining <= 0) {
          // Let the caller decide what to do — do NOT logout here (side-effects
          // in a getter cause infinite loops).
          return {};
        }
        const fakeExpiry = dayjs(this._testStartTime).add(
          this._testDuration,
          "seconds"
        );
        return { ...realProfile, validTill: fakeExpiry.toISOString() };
      }

      return realProfile;
    } catch (err) {
      console.error("Error parsing profile details:", err);
      return {};
    }
  }

  /** Returns seconds until token expiry (>=0), or -1 if unknown. */
  getSecondsUntilExpiry() {
    const profile = this.getProfileDetails();
    if (!profile?.validTill) return -1;
    return dayjs(profile.validTill).diff(dayjs(), "seconds");
  }

  /* =========================================================
   *  CROSS-TAB SYNC
   * ========================================================= */

  setupCrossTabSync() {
    window.addEventListener("storage", this._boundHandlers.storage);

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && this.isInitialized) {
        this.syncSessionFromStorage();
        if (!this.isDialogVisible) this.resetInactivityTimer();
      }
    });
  }

  handleStorageEvent(event) {
    if (!this.isInitialized) return;

    if (event.key === this.CROSS_TAB_LOGOUT_KEY && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        if (data.tabId !== this.tabId) this.handleCrossTabLogout(data);
      } catch (err) {
        console.error("Error parsing logout event:", err);
      }
    } else if (event.key === this.CROSS_TAB_EXTEND_KEY && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        if (data.tabId !== this.tabId) this.handleCrossTabExtension(data);
      } catch (err) {
        console.error("Error parsing extend event:", err);
      }
    } else if (event.key === this.CROSS_TAB_SYNC_KEY && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        if (data.tabId !== this.tabId) this.syncSessionFromStorage();
      } catch (err) {
        console.error("Error parsing sync event:", err);
      }
    }
  }

  handleCrossTabLogout(logoutData) {
    this.isInitialized = false;
    this.isLoggingOut  = true;

    this.stopCountdown();
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    this.hideDialog();

    if (this.onCrossTabLogout) this.onCrossTabLogout(logoutData);
    if (this.onSessionExpired) this.onSessionExpired();

    this.isLoggingOut = false;
  }

  handleCrossTabExtension(extendData) {
    if (extendData.token && extendData.profileDetails) {
      localStorage.setItem("access-token", extendData.token);
      localStorage.setItem(
        "profileDetails",
        JSON.stringify(extendData.profileDetails)
      );
    }

    this.resetInactivityTimer();
    if (this.isDialogVisible) this.hideDialog();

    if (this.onCrossTabExtend) this.onCrossTabExtend(extendData);
  }

  broadcastToOtherTabs(eventType, data) {
    const payload = { ...data, tabId: this.tabId, timestamp: Date.now() };
    const key =
      eventType === "logout"
        ? this.CROSS_TAB_LOGOUT_KEY
        : eventType === "extend"
        ? this.CROSS_TAB_EXTEND_KEY
        : eventType === "sync"
        ? this.CROSS_TAB_SYNC_KEY
        : null;

    if (!key) return;

    localStorage.setItem(key, JSON.stringify(payload));
    setTimeout(() => localStorage.removeItem(key), 100);
  }

  syncSessionFromStorage() {
    if (!this.isInitialized || this.isLoggingOut) return;

    const profile = this.getProfileDetails();
    const token   = localStorage.getItem("access-token");

    if (!token || !profile?.validTill) {
      this.logout();
      return;
    }

    const secondsLeft = this.getSecondsUntilExpiry();

    if (secondsLeft <= 0) {
      this.logout();
      return;
    }

    // ---- FIX: compare in SECONDS (WARNING_BEFORE_EXPIRY is ms) ----
    const warnSeconds = this.WARNING_BEFORE_EXPIRY / 1000;
    const idleSeconds = Date.now() - this.lastActivityTime;

    if (
      secondsLeft <= warnSeconds &&
      idleSeconds >= this.INACTIVITY_TIMEOUT &&
      !this.isDialogVisible
    ) {
      this.showCountdown();
    } else if (!this.isDialogVisible) {
      this.resetInactivityTimer();
    }
  }

  /* =========================================================
   *  EVENT LISTENERS
   * ========================================================= */

  attachEventListeners() {
    this.removeEventListeners();
    this.events.forEach((ev) =>
      document.addEventListener(ev, this._boundHandlers.activity)
    );
    document.addEventListener(
      "visibilitychange",
      this._boundHandlers.visibility
    );
    window.addEventListener("beforeunload", this._boundHandlers.beforeUnload);
  }

  removeEventListeners() {
    this.events.forEach((ev) =>
      document.removeEventListener(ev, this._boundHandlers.activity)
    );
    document.removeEventListener(
      "visibilitychange",
      this._boundHandlers.visibility
    );
    window.removeEventListener(
      "beforeunload",
      this._boundHandlers.beforeUnload
    );
  }

  handleUserActivity() {
    if (!this.isInitialized || this.isLoggingOut) return;

    this.lastActivityTime = Date.now();
    this.isUserActive     = true;

    if (this.isDialogVisible) {
      console.log(`Tab ${this.tabId}: Hiding dialog due to user activity`);
      // ---- FIX: hideDialog() already resets the timer; don't call twice ----
      this.hideDialog();
      if (!this.isExtending) {
        // Small delay so the dialog close animation finishes first
        setTimeout(() => this.extendSession(), 100);
      }
    } else {
      this.resetInactivityTimer();
    }

    if (this.onUserActivity) this.onUserActivity();
  }

  handleVisibilityChange() {
    if (!this.isInitialized) return;
    if (!document.hidden) {
      this.syncSessionFromStorage();
      if (!this.isDialogVisible) this.resetInactivityTimer();
    }
  }

  handleBeforeUnload() {
    this.stopCountdown();
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /* =========================================================
   *  INACTIVITY / COUNTDOWN
   * ========================================================= */

  resetInactivityTimer() {
    if (!this.isInitialized || this.isLoggingOut) return;

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    // ---- FIX: do NOT call hideDialog() here (recursion risk).
    //     hideDialog() calls resetInactivityTimer(); instead, close the dialog
    //     inline and let this method own the timer. ----
    if (this.isDialogVisible) {
      this.isDialogVisible = false;
      this.stopCountdown();
      this.countdownSeconds = this.COUNTDOWN_DURATION / 1000;
      if (this.onHideDialog) this.onHideDialog();
    }

    this.inactivityTimer = setTimeout(() => {
      const idle = Date.now() - this.lastActivityTime;
      if (idle >= this.INACTIVITY_TIMEOUT) {
        this.showCountdown();
      } else {
        this.resetInactivityTimer();
      }
    }, this.INACTIVITY_TIMEOUT);
  }

  showCountdown() {
    if (!this.isInitialized || this.isLoggingOut) return;

    if (this.isDialogVisible) {
      if (!this.countdownTimer) this.startCountdown();
      return;
    }

    const profile = this.getProfileDetails();
    const token   = localStorage.getItem("access-token");
    if (!token || !profile?.validTill) {
      this.logout();
      return;
    }

    const secondsLeft = this.getSecondsUntilExpiry();
    if (secondsLeft <= 0) {
      this.logout();
      return;
    }

    console.log(
      `Tab ${this.tabId}: Showing session timeout dialog - ${secondsLeft}s remaining`
    );

    this.isDialogVisible     = true;
    this.countdownStartTime  = Date.now();
    this.lastCountdownUpdate = Date.now();
    this.lastDialogShowTime  = Date.now();

    // ---- FIX: COUNTDOWN_DURATION is ms; countdownSeconds is seconds ----
    const countdownSec = this.COUNTDOWN_DURATION / 1000;
    this.countdownSeconds = Math.min(countdownSec, Math.max(0, secondsLeft));

    if (this.onShowDialog) this.onShowDialog();
    this.startCountdown();
  }

  startCountdown() {
    this.stopCountdown();

    const secondsLeft = this.getSecondsUntilExpiry();
    const countdownSec = this.COUNTDOWN_DURATION / 1000;

    this.countdownSeconds =
      secondsLeft >= 0
        ? Math.min(countdownSec, secondsLeft)
        : countdownSec;

    this.countdownStartTime  = Date.now();
    this.lastCountdownUpdate = Date.now();

    console.log(
      `Tab ${this.tabId}: Countdown started with ${this.countdownSeconds}s`
    );

    this.countdownTimer = setInterval(
      () => this.updateCountdown(),
      this.COUNTDOWN_UPDATE_INTERVAL
    );
  }

  updateCountdown() {
    if (!this.isInitialized || !this.isDialogVisible || this.isLoggingOut) {
      this.stopCountdown();
      return;
    }

    // If user became active while the dialog was open, dismiss & extend
    const idle = Date.now() - this.lastActivityTime;
    if (idle < this.INACTIVITY_TIMEOUT) {
      console.log(`Tab ${this.tabId}: User active, hiding dialog`);
      this.hideDialog();
      return;
    }

    const secondsLeft = this.getSecondsUntilExpiry();
    let remaining;

    if (secondsLeft >= 0) {
      remaining = Math.min(this.COUNTDOWN_DURATION / 1000, secondsLeft);
    } else {
      // Fallback: decrement locally
      const now     = Date.now();
      const elapsed = (now - this.lastCountdownUpdate) / 1000;
      this.lastCountdownUpdate = now;
      remaining = Math.max(0, this.countdownSeconds - elapsed);
    }

    this.countdownSeconds = remaining;
    const displaySeconds  = Math.ceil(remaining);

    if (this.onTokenExpiring) this.onTokenExpiring(displaySeconds);

    if (remaining <= 0) {
      console.log(`Tab ${this.tabId}: Countdown finished - auto logging out`);
      this.stopCountdown();
      this.logout();
    }
  }

  getRemainingTime() {
    if (this.isDialogVisible) {
      const secondsLeft = this.getSecondsUntilExpiry();
      if (secondsLeft >= 0) {
        return Math.min(this.COUNTDOWN_DURATION / 1000, secondsLeft);
      }
      if (this.countdownStartTime) {
        const elapsed =
          (Date.now() - this.countdownStartTime) / 1000;
        return Math.max(0, this.countdownSeconds - elapsed);
      }
      return this.COUNTDOWN_DURATION / 1000;
    }

    // ---- FIX: when no dialog is visible, return time until token expiry ----
    const secondsLeft = this.getSecondsUntilExpiry();
    return secondsLeft >= 0 ? secondsLeft : this.COUNTDOWN_DURATION / 1000;
  }

  stopCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdownStartTime  = null;
    this.lastCountdownUpdate = null;
  }

  hideDialog() {
    if (!this.isDialogVisible) return;

    this.isDialogVisible  = false;
    this.stopCountdown();
    this.countdownSeconds = this.COUNTDOWN_DURATION / 1000;

    if (this.onHideDialog) this.onHideDialog();

    // Re-arm the inactivity timer
    this.resetInactivityTimer();
  }

  /* =========================================================
   *  TOKEN EXPIRY POLL
   * ========================================================= */

  startTokenExpiryCheck() {
    if (this.tokenCheckInterval) clearInterval(this.tokenCheckInterval);
    this.tokenCheckInterval = setInterval(
      () => this.checkTokenExpiry(),
      this.TOKEN_CHECK_INTERVAL
    );
  }

  checkTokenExpiry() {
    try {
      if (!this.isInitialized || this.isLoggingOut) return;
      if (this.isDialogVisible) return;

      const secondsLeft = this.getSecondsUntilExpiry();
      if (secondsLeft < 0) return;

      const idle = Date.now() - this.lastActivityTime;
      const warnSeconds = this.WARNING_BEFORE_EXPIRY / 1000;

      if (
        secondsLeft <= warnSeconds &&
        secondsLeft > 0 &&
        idle >= this.INACTIVITY_TIMEOUT
      ) {
        console.log(
          `Tab ${this.tabId}: Token expires in ${secondsLeft}s - showing warning`
        );
        this.showCountdown();
      } else if (secondsLeft <= 0) {
        console.log(`Tab ${this.tabId}: Token expired - logging out`);
        this.logout();
      }
    } catch (err) {
      console.error("Error checking token expiry:", err);
    }
  }

  /* =========================================================
   *  EXTEND / LOGOUT
   * ========================================================= */

  async extendSession() {
    if (!this.isInitialized || this.isExtending || this.isLoggingOut) {
      return false;
    }

    console.log(`Tab ${this.tabId}: Extending session...`);
    this.isExtending = true;

    try {
      if (this._testMode) {
        this._testStartTime = Date.now();
        sessionStorage.setItem(
          "test_start_time",
          String(this._testStartTime)
        );
        console.log(
          `Tab ${this.tabId}: Test session extended for ${this._testDuration}s`
        );
        this.hideDialog();
        this.isExtending = false;
        return true;
      }

      const response = await extendToken_API();
      const newToken = response?.data?.authToken;
      const profile  = response?.data;

      if (newToken && profile) {
        localStorage.setItem("access-token", newToken);
        localStorage.setItem("profileDetails", JSON.stringify(profile));

        console.log(`Tab ${this.tabId}: Session extended. New expiry: ${profile.validTill}`);

        this.broadcastToOtherTabs("extend", {
          token: newToken,
          profileDetails: profile,
        });

        this.hideDialog();
        this.isExtending = false;
        return true;
      }

      console.warn(`Tab ${this.tabId}: Extension response missing token/profile`);
      this.isExtending = false;
      return false;
    } catch (err) {
      console.error(`Tab ${this.tabId}: Session extension failed:`, err);
      this.isExtending = false;
      return false;
    }
  }

  logout() {
    if (this.isLoggingOut) return;

    console.log(`Tab ${this.tabId}: Session logout triggered`);
    this.isLoggingOut = true;

    // Broadcast FIRST, then clear local state
    this.broadcastToOtherTabs("logout", {
      message: "User logged out from another tab",
    });

    this.stopCountdown();
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    localStorage.removeItem("access-token");
    localStorage.removeItem("profileDetails");

    if (this._testMode) {
      sessionStorage.removeItem("test_mode");
      sessionStorage.removeItem("test_start_time");
      localStorage.removeItem("test_mode");
    }

    this.hideDialog();
    this.isInitialized = false;

    if (this.onSessionExpired) this.onSessionExpired();

    this.isLoggingOut = false;
  }

  /* =========================================================
   *  PUBLIC API
   * ========================================================= */

  resetTimer() {
    this.resetInactivityTimer();
  }

  isSessionActive() {
    return this.isInitialized && !this.isDialogVisible;
  }

  getTabId() {
    return this.tabId;
  }

  forceSync() {
    this.broadcastToOtherTabs("sync", {});
    this.syncSessionFromStorage();
  }

  getConfig() {
    return {
      testMode: this._testMode,
      testStartTime: this._testStartTime,
      testRemaining: this.getTestRemainingSeconds(),
      inactivityTimeout: this.INACTIVITY_TIMEOUT / 1000 + " seconds",
      countdownDuration: this.COUNTDOWN_DURATION / 1000 + " seconds",
      warningBeforeExpiry: this.WARNING_BEFORE_EXPIRY / 1000 + " seconds",
      isDialogVisible: this.isDialogVisible,
      isInitialized: this.isInitialized,
      isExtending: this.isExtending,
      countdownSeconds: this.countdownSeconds,
      secondsUntilExpiry: this.getSecondsUntilExpiry(),
    };
  }
}

/* =========================================================
 *  SINGLETON + DEBUG HOOKS
 *  FIX: create the instance ONCE and share it with window.
 * ========================================================= */

const sessionManager = new SessionManager();

if (typeof window !== "undefined") {
  window.__sessionManager = {
    instance: sessionManager,

    enableTest: () => {
      sessionManager.enableTestMode();
      console.log(" Test mode enabled! Session will expire in 60 seconds");
      if (sessionManager.isInitialized) {
        sessionManager.resetInactivityTimer();
      }
      return sessionManager.getConfig();
    },

    disableTest: () => {
      sessionManager.disableTestMode();
      console.log(" Test mode disabled. 10 minute timeout restored.");
      return sessionManager.getConfig();
    },

    config: () => sessionManager.getConfig(),

    forceLogout: () => sessionManager.logout(),

    getRemaining: () => sessionManager.getRemainingTime(),
  };
}

export default sessionManager;